import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useForm } from 'react-hook-form';
import { Mail, Lock, Eye, EyeOff, Phone, UtensilsCrossed, ShoppingCart, BarChart3, Users } from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import EngiSpiderIcon from '../assets/EngiSpider_Icon.png';
import EngiSpiderIconWhite from '../assets/EngiSpider Wht.png';
import { ButtonLoader } from '../components/LoadingStates';

interface LoginForm {
  emailOrPhone: string;
  password: string;
}

export const Login: React.FC = () => {
  const { login } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState<'email' | 'phone'>('email');

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<LoginForm>();

  const onSubmit = async (data: LoginForm) => {
    try {
      setIsLoading(true);
      
      await login(data.emailOrPhone, data.password);
      
      const from = location.state?.from?.pathname || '/';
      navigate(from, { replace: true });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <img src={EngiSpiderIcon} alt="EngiSpider" className="h-25 w-20" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Welcome to Restro360
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your restaurant management account
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              {/* Login Method Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setLoginMethod('email')}
                  className={clsx(
                    'flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-colors',
                    loginMethod === 'email'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </button>
                <button
                  type="button"
                  onClick={() => setLoginMethod('phone')}
                  className={clsx(
                    'flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-colors',
                    loginMethod === 'phone'
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Phone className="h-4 w-4 mr-1" />
                  Phone
                </button>
              </div>

              {/* Email/Phone Input */}
              <div>
                <label htmlFor="emailOrPhone" className="block text-sm font-medium text-gray-700">
                  {loginMethod === 'email' ? 'Email address' : 'Phone number'}
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {loginMethod === 'email' ? (
                      <Mail className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Phone className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                  <input
                    {...register('emailOrPhone', {
                      required: `${loginMethod === 'email' ? 'Email' : 'Phone number'} is required`,
                      pattern: loginMethod === 'email' ? {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      } : {
                        value: /^[0-9]{10}$/,
                        message: 'Invalid phone number (10 digits required)'
                      }
                    })}
                    type={loginMethod === 'email' ? 'email' : 'tel'}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={loginMethod === 'email' ? 'Enter your email' : 'Enter your phone number'}
                  />
                </div>
                {errors.emailOrPhone && (
                  <p className="mt-1 text-sm text-red-600">{errors.emailOrPhone.message}</p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 6,
                        message: 'Password must be at least 6 characters'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm font-medium transition-colors" style={{ color: currentTheme.colors.primary[600] }}>
                Forgot password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{
                  backgroundColor: currentTheme.colors.primary[600],
                  borderColor: currentTheme.colors.primary[500]
                }}
                onMouseEnter={(e) => !isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[700])}
                onMouseLeave={(e) => !isLoading && (e.currentTarget.style.backgroundColor = currentTheme.colors.primary[600])}
                onFocus={(e) => e.currentTarget.style.boxShadow = `0 0 0 2px ${currentTheme.colors.primary[100]}`}
                onBlur={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                {isLoading ? (
                  <ButtonLoader text="Signing in..." />
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            {/* Regitrstion removed from here */}
            {/* <div className="text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
                  Register here
                </Link>
              </p>
            </div> */}
          </form>
        </div>
      </div>

      {/* Right side - Image/Illustration */}
      <div className="hidden lg:block lg:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="flex items-center justify-center h-full p-12">
          <div className="text-center text-white">
            <img src={EngiSpiderIconWhite} alt="EngiSpider" className="h-32 w-62 mx-auto mb-8 opacity-90" style={{ filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.3))' }} />
            <h1 className="text-4xl font-bold mb-4">Restaurant Management Made Easy</h1>
            <p className="text-xl opacity-90 mb-8">
              Streamline your restaurant operations with our comprehensive ERP solution
            </p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-white/10 rounded-lg p-4">
                <UtensilsCrossed className="h-8 w-8 mx-auto mb-2" />
                <div>Menu Management</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <ShoppingCart className="h-8 w-8 mx-auto mb-2" />
                <div>Order Processing</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <BarChart3 className="h-8 w-8 mx-auto mb-2" />
                <div>Sales Analytics</div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <Users className="h-8 w-8 mx-auto mb-2" />
                <div>Table Management</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};